import { useCallback } from "react";
import useSWRMutation from "swr/mutation";
import * as Sentry from "@sentry/react";
import { Utils } from "../common/utils";
import { EAIClient } from "Services/EAIClient";
import { useAppContext } from "@/contexts/app";
import { StandardResponse } from "../types/swr";

const clientConfigs = Utils.getEAIClientConfigs();
const client = new EAIClient();

export type UploadFileResponse = {
    rowCount: number;
};

interface UploadFileProps {
    file: File;
}

export function useUploadFile() {
    const { dispatchError } = useAppContext();
    const { trigger, isMutating, data } = useSWRMutation<
        StandardResponse<UploadFileResponse>,
        Error,
        string,
        FormData
    >(
        `${clientConfigs.endpoint}/schools/${clientConfigs.school}/curriculum_mapping/upload`,
        (_, { arg }) => client.uploadCurriculumMapping(arg)
    );

    const uploadFile = useCallback(async ({ file }: UploadFileProps) => {
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await trigger(formData);
            if (!response?.data) throw new Error("Upload failed");

            return "File uploaded successfully.";
        } catch (error: any) {
            const message = error?.result?.message || "Upload failed";
            Sentry.captureException(error);
            dispatchError(message);
            throw new Error(message);
        }
    }, [trigger, dispatchError]);

    return {
        uploadFile,
        isUploading: isMutating,
        rowCount: data?.data?.rowCount
    };
}